import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IoBagHandleOutline } from "react-icons/io5";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";
import { addToCart } from "../../api/cartApi";
import { getProducts } from "../../api/productApi";
import useSponsoredAds from "../../hook/useSponsoredAds";
import AdOptionsMenu from "../ads/AdOptionsMenu";

const initialProducts = [];

const AddToCartButton = ({ productId }) => {
  const [status, setStatus] = useState("idle");
  const busy = useRef(false);
  const timer = useRef(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  const handleAdd = async () => {
    if (busy.current) return;
    busy.current = true;
    setStatus("pending");
    try {
      const response = await addToCart(productId);
      if (!mounted.current) return;
      if (!response?.success) throw new Error(response?.message || "Failed to add to cart.");
      setStatus("added");
      timer.current = setTimeout(() => {
        busy.current = false;
        setStatus("idle");
      }, 1500);
    } catch (error) {
      if (!mounted.current) return;
      busy.current = false;
      setStatus("idle");
      alert(error.message || "Failed to add to cart. Please try again.");
    }
  };

  return (
    <button
      type="button"
      className={`flex gap-2 items-center rounded-xl text-xs mt-2 p-2 ${status === "added" ? "bg-[#86efac] text-[#161F28]" : "bg-[#161F28] text-white"}`}
      onClick={handleAdd}
      disabled={status !== "idle"}
      aria-busy={status === "pending"}
    >
      {status === "added" ? (
        <span className="text-lg" aria-hidden="true">✓</span>
      ) : (
        <IoBagHandleOutline className="text-lg" aria-hidden="true" />
      )}
      <span aria-live="polite">{status === "added" ? "Added to cart" : "Add to cart"}</span>
    </button>
  );
};

const AllProduct = ({ title, categoryId }) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [products, setProducts] = useState(initialProducts);
  const [loading, setLoading] = useState(false);
  const itemsToShow = 5;

  // Category tab -> "listing" placement (the API requires category_id); the "All" tab has no
  // category, so it uses the "feed" placement (sellers' "Marketplace Feed" option).
  const { ads: sponsoredAds, trackEvent, removeAd } = useSponsoredAds({
    placement: categoryId ? "listing" : "feed",
    context: categoryId ? { category_id: Number(categoryId) } : { page: "marketplace" },
    count: 2,
    productOnly: true,
  });
  const sponsoredProductIds = new Set(sponsoredAds.map((ad) => ad.product?.id));
  // Sponsored cards lead the carousel; the same product isn't repeated as an organic card.
  const displayProducts = [
    ...sponsoredAds
      .filter((ad) => ad.product)
      .map((ad) => ({ ...mapApiProduct(ad.product), sponsoredAd: ad })),
    ...products.filter((product) => !sponsoredProductIds.has(product.id)),
  ];

  // Guard the modulo: with an empty list it would set current to NaN and blank the carousel.
  const nextSlide = () =>
    displayProducts.length && setCurrent((prev) => (prev + itemsToShow) % displayProducts.length);
  const prevSlide = () =>
    displayProducts.length &&
    setCurrent(
      (prev) => (prev - itemsToShow + displayProducts.length) % displayProducts.length
    );

  const totalDots = Math.ceil(displayProducts.length / itemsToShow);

  // Map API product shape to UI-friendly shape
  function mapApiProduct(p) {
    const originalPrice = Number(p.product_price) || 0;
    const discountedPrice = Number(p.discount_price) || 0;
    const offerPercentage =
      p.offer_percentage !== undefined
        ? Number(p.offer_percentage)
        : originalPrice
        ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
        : 0;

    return {
      id: p.id,
      name: p.product_name || p.title || "Untitled",
      image:
        (p.product_media && p.product_media[0] && p.product_media[0].url) ||
        "/image/shoeimg.png",
      originalPrice: originalPrice.toFixed
        ? originalPrice.toFixed(2)
        : String(originalPrice),
      discountedPrice: discountedPrice.toFixed
        ? discountedPrice.toFixed(2)
        : String(discountedPrice),
      offerPercentage,
      inStock: p.inStock !== undefined ? p.inStock : p.product_qty > 0,
      raw: p,
      sellername: p.seller?.name || "Unknown Seller", // ✅ Get seller name dynamically
    };
  }

  // Fetch products when component mounts or categoryId changes
  useEffect(() => {
    let mounted = true;
    // New tab: start from the first card and drop the previous tab's products, otherwise the
    // old carousel position can point past the end of a shorter list and show nothing.
    setCurrent(0);
    setProducts([]);
    async function load() {
      setLoading(true);
      try {
        const filters = categoryId ? { category_id: categoryId } : {};
        const res = await getProducts(filters, 1, 20);
        const apiList =
          res?.result?.products ||
          res?.result?.Products ||
          res?.products ||
          res?.data?.products ||
          res?.data ||
          res;
        if (mounted && Array.isArray(apiList)) {
          setProducts(apiList.map(mapApiProduct));
        }
      } catch (err) {
        console.error("Failed to load products:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [categoryId]);

  // 🔹 Handle navigation to ProductView
  // const handleBuyNow = (productId) => {
  //   navigate(`/ProductView`, {
  //     state: { productId },
  //   });
  // };

  const handleBuyNow = (productId, sponsoredAd) => {
   if (sponsoredAd) trackEvent(sponsoredAd);
   navigate(`/ProductView/${productId}`, {
      state: { productId },
    });
    console.log("productId",productId)
  };
  // Get visible products in window view
  // Wrap-around only when there are more products than slots - with fewer, wrapping repeats
  // the same product (and duplicates its React key, leaving stale cards behind on tab change).
  const visibleProducts = displayProducts.length <= itemsToShow
    ? displayProducts
    : displayProducts
        .slice(current, current + itemsToShow)
        .concat(
          current + itemsToShow > displayProducts.length
            ? displayProducts.slice(0, (current + itemsToShow) % displayProducts.length)
            : []
        );

  return (
    <div className="mt-5 w-full  mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold border-b-2 border-green-500">
          {/* {` New Arrivals ${title}`} */}
          New Arrivals
        </h2>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/shoppage")}
            className="border-2 text-md border-green-300 px-3 py-2  bg-green-100 rounded-full"
          >
            View All
          </button>
          <button
            onClick={prevSlide}
            className="border-2 border-green-300 px-3 py-2  bg-green-100 rounded-full"
          >
            <MdArrowBackIosNew />
          </button>
          <button
            onClick={nextSlide}
            className="border-2 border-green-300 px-3 py-2  bg-green-100 rounded-full"
          >
            <MdArrowForwardIos />
          </button>
        </div>
      </div>

      {!loading && displayProducts.length === 0 && (
        <div className="p-4 text-center">No products found in this category.</div>
      )}

      {/* Carousel */}
      <div className="flex gap-4 overflow-hidden">
        {visibleProducts.map((product) => (
          <div
            key={product.id}
            className="flex-shrink-0 w-[90%] sm:w-[45%] md:w-[45%] lg:w-[30%] xl:w-[23%] border-2 border-purple-600 rounded-md p-1 bg-white flex-shrink-0"
          >
            <div
              className="bg-[#eceef0] relative rounded-xl cursor-pointer"
              onClick={() => handleBuyNow(product.id, product.sponsoredAd)}
            >
              {product.sponsoredAd && (
                <span className="absolute top-2 right-2 bg-[#F97316] text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                  Sponsored
                </span>
              )}
              {product.sponsoredAd && (
                <AdOptionsMenu
                  ad={product.sponsoredAd}
                  className="bottom-2 right-2"
                  dropdownClassName="right-0 bottom-full mb-1"
                  onHideAd={() => removeAd(product.sponsoredAd.trackingId)}
                />
              )}
              <p className="absolute top-1 left-1 p-2 rounded-tl-3xl rounded-br-3xl bg-red-700 text-white text-xs font-semibold px-3">
                {product.offerPercentage} % Offer{" "}
              </p>
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-48 object-contain"
              />
            </div>
            <div className="p-2 space-y-2">
              <div className="inline-flex px-2 py-1 rounded-xl gap-1 items-center border-[1px] border-[#08AEEA] ">
                <img src="/image/wanchain.png" alt="" className="w-4 h-4" />
                <h4 className="text-[10px] font-bold">{product.sellername}</h4>
              </div>
              <h4 className="text-[13px] font-bold">{product.name}</h4>

              <p className="text-xs font-semibold line-through text-gray-400">
                ${product.originalPrice}
              </p>
              <div className="flex justify-between items-center">
                <p className="text-[15px] font-bold text-[#095228]">
                  ${product.discountedPrice}
                </p>
                <AddToCartButton productId={product.id} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dots */}
      <div className="flex space-x-2 justify-start mt-4">
        {Array.from({ length: totalDots }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx * itemsToShow)} // jump to group
            className={`h-2 rounded-full transition-all duration-300 ${
              Math.floor(current / itemsToShow) === idx
                ? "bg-green-600 w-[20px]"
                : "bg-gray-300 w-2"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
};

export default AllProduct;
